import dayjs from 'dayjs';
import type {
	ConditionProperties,
	EngineOptions,
	RuleProperties,
	TopLevelCondition as TLC,
} from 'json-rules-engine';
import { Engine as E } from 'json-rules-engine';
import isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import unionWith from 'lodash/unionWith';

import type { Conditions, TopLevelCondition } from './types';
import { ConditionOperators } from './types';

class Engine extends E {
	constructor(rules?: RuleProperties[], options?: EngineOptions) {
		super(rules, options);
		// Bind methods to preserve 'this' context when used as callbacks
		this.isSameType = this.isSameType.bind(this);
		this.isEqual = this.isEqual.bind(this);
		this.overrideOperators();
	}

	/* Determine two values in same data types */
	private isSameType(a: any, b: any) {
		return typeof a === typeof b;
	}

	/* Determine two object are equal */
	private isEqual(a: any, b: any) {
		if (!this.isSameType(a, b)) {
			/* Handle string boolean */
			if (typeof a === 'boolean') {
				const bool = b === 'true' ? true : b === 'false' ? false : undefined;
				return a === bool;
			}

			/**
			 * Handle special cases, e.g.:
			 * - false == "false"
			 * - 0 == "0"
			 */
			return a == b;
		}

		// Handle non object types
		switch (typeof a) {
			case 'string':
				return a.trim() === b.trim();
			case 'boolean':
			case 'number':
			case 'bigint':
			case 'undefined':
				return a === b;
			case 'symbol':
			case 'function':
				return _isEqual(a, b);
		}

		// Handle object and array
		if (!Array.isArray(a) && !Array.isArray(b)) {
			return _isEqual(a, b);
		} else if (Array.isArray(a) && Array.isArray(b)) {
			if (a.length !== b.length) {
				return false;
			} else {
				return a.every((_, i) => {
					return _isEqual(a[i], b[i]);
				});
			}
		}

		// fallback
		return _isEqual(a, b);
	}

	/* Determine second value contains first value */
	private isContains(a: any, b: any) {
		if (isNil(b)) return false;

		const bType = typeof b;

		// Handle non string, array amd object value
		switch (bType) {
			case 'number':
			case 'bigint':
			case 'symbol':
			case 'undefined':
			case 'boolean':
			case 'function':
				return false;
		}

		if (this.isSameType(a, b)) {
			switch (bType) {
				case 'string':
					return b.trim().includes(a.trim());
				case 'object':
					if (!Array.isArray(a) && !Array.isArray(b)) {
						return Object.entries(b).some(([key, value]) =>
							this.isEqual(value, a?.[key])
						);
					} else if (Array.isArray(a) && Array.isArray(b)) {
						return unionWith(a, b, this.isEqual).length > 0;
					} else if (Array.isArray(b)) {
						return b.some((value) => this.isEqual(value, a));
					}
					return false;
				default:
					return false;
			}
		} else if (bType === 'object' && Array.isArray(b)) {
			return b.some((value) => this.isEqual(value, a));
		}

		return false;
	}

	/* Determine the value is Unix timestamp (is seconds) */
	private isValidUnixTimestamp(value: any) {
		if (typeof value !== 'number' || isNaN(value)) {
			return false;
		}

		const MIN_TIMESTAMP_SECONDS = -2208988800; // Represents roughly 1900-01-01
		const MAX_TIMESTAMP_SECONDS = 4102444800; // Represents roughly 2100-01-01

		if (value < MIN_TIMESTAMP_SECONDS || value > MAX_TIMESTAMP_SECONDS) {
			return false;
		}

		return true;
	}

	/* Transform any value to dayjs.configType */
	private toDayjsConfigType(value: any): dayjs.ConfigType {
		switch (typeof value) {
			case 'object':
				if (value instanceof Date) {
					return value;
				}
				return undefined;
			case 'string':
				if (dayjs(value).isValid()) {
					return value;
				}
				return undefined;
			case 'number':
				if (this.isValidUnixTimestamp(value)) {
					// Transform to Unix timestamp in milliseconds
					return Math.round(value * 1000);
				}
				return undefined;
			case 'bigint':
			case 'function':
			case 'symbol':
			case 'undefined':
			default:
				return undefined;
		}
	}

	/**
	 * Override and add operators for the json-rules-engine
	 * Reference: https://github.com/CacheControl/json-rules-engine/blob/master/docs/rules.md#operators
	 */
	private overrideOperators() {
		/**
		 * Add startWith operator, supports:
		 * - String
		 */
		this.addOperator(
			ConditionOperators.StartWith,
			(factValue: any, compareToValue: any) => {
				if (
					!this.isSameType(factValue, compareToValue) ||
					typeof factValue !== 'string'
				) {
					return false;
				}

				const _factValue = factValue.trim();
				const _compareToValue = compareToValue.trim();

				return _factValue.startsWith(_compareToValue);
			}
		);

		/**
		 * Add EndWith operator, supports:
		 * - String
		 */
		this.addOperator(
			ConditionOperators.EndWith,
			(factValue: any, compareToValue: any) => {
				if (
					!this.isSameType(factValue, compareToValue) ||
					typeof factValue !== 'string'
				) {
					return false;
				}

				const _factValue = factValue.trim();
				const _compareToValue = compareToValue.trim();

				return _factValue.endsWith(_compareToValue);
			}
		);

		/**
		 * Add isEmpty operator, supports:
		 * - String
		 * - Number
		 * - Array
		 * - Object
		 */
		this.addOperator(ConditionOperators.IsEmpty, (factValue: any) => {
			if (!isNil(factValue)) {
				switch (typeof factValue) {
					case 'string':
						return factValue.trim() === '';
					case 'object':
						return isEmpty(factValue);
					case 'boolean':
					case 'number':
					case 'bigint':
					case 'symbol':
					case 'function':
					default:
						return true;
				}
			}

			return true;
		});

		/**
		 * Add isOverdue operator, supports:
		 * - String (date string)
		 * - Number (Unix timestamp in seconds)
		 * - Object (Date)
		 */
		this.addOperator(ConditionOperators.IsOverdue, (factValue: any) => {
			if (isNil(factValue)) return false;

			const _factValue = this.toDayjsConfigType(factValue);
			return !isNil(_factValue) ? dayjs(_factValue).isAfter(dayjs()) : false;
		});

		/**
		 * Add isNotOverdue operator, supports:
		 * - String (date string)
		 * - Number (Unix timestamp in seconds)
		 * - Object (Date)
		 */
		this.addOperator(ConditionOperators.IsNotOverdue, (factValue: any) => {
			if (isNil(factValue)) return false;

			const _factValue = this.toDayjsConfigType(factValue);
			return !isNil(_factValue) ? dayjs(_factValue).isBefore(dayjs()) : false;
		});

		/**
		 * Override equal operator, supports:
		 * - String
		 * - Number
		 * - Object
		 * - Array
		 */
		this.addOperator(
			ConditionOperators.Equal,
			(factValue: any, compareToValue: any) => {
				if (isNil(factValue)) return false;

				return this.isEqual(factValue, compareToValue);
			}
		);

		/**
		 * Override notEqual operator, supports:
		 * - String
		 * - Number
		 * - Object
		 * - Array
		 */
		this.addOperator(
			ConditionOperators.NotEqual,
			(factValue: any, compareToValue: any) => {
				if (isNil(factValue)) return false;

				return !this.isEqual(factValue, compareToValue);
			}
		);

		/**
		 * Override lessThan operator, supports:
		 * - Number
		 */
		this.addOperator(
			ConditionOperators.LessThan,
			(factValue: any, compareToValue: any) => {
				if (isNil(compareToValue)) return false;

				if (
					!this.isSameType(factValue, compareToValue) ||
					!['bigint', 'number'].includes(typeof factValue)
				) {
					return false;
				}

				return factValue < compareToValue;
			}
		);

		/**
		 * Override lessThanInclusive operator, supports:
		 * - Number
		 */
		this.addOperator(
			ConditionOperators.LessThanInclusive,
			(factValue: any, compareToValue: any) => {
				if (
					!this.isSameType(factValue, compareToValue) ||
					!['bigint', 'number'].includes(typeof factValue)
				) {
					return false;
				}

				return factValue <= compareToValue;
			}
		);

		/**
		 * Override greaterThan operator, supports:
		 * - Number
		 */
		this.addOperator(
			ConditionOperators.GreaterThan,
			(factValue: any, compareToValue: any) => {
				if (
					!this.isSameType(factValue, compareToValue) ||
					!['bigint', 'number'].includes(typeof factValue)
				) {
					return false;
				}

				return factValue > compareToValue;
			}
		);

		/**
		 * Override greaterThanInclusive operator, supports:
		 * - Number
		 */
		this.addOperator(
			ConditionOperators.GreaterThanInclusive,
			(factValue: any, compareToValue: any) => {
				if (
					!this.isSameType(factValue, compareToValue) ||
					!['bigint', 'number'].includes(typeof factValue)
				) {
					return false;
				}

				return factValue >= compareToValue;
			}
		);

		/**
		 * Override contains operator, supports:
		 * - String
		 * - Array
		 * - Object
		 */
		this.addOperator(
			ConditionOperators.Contains,
			(factValue: any, compareToValue: any) => {
				return this.isContains(compareToValue, factValue);
			}
		);

		/**
		 * Override doesNotContains operator, supports:
		 * - String
		 * - Array
		 * - Object
		 */
		this.addOperator(
			ConditionOperators.DoesNotContain,
			(factValue: any, compareToValue: any) => {
				return !this.isContains(compareToValue, factValue);
			}
		);

		/**
		 * Override in operator, supports nothing
		 * As no use case at all
		 */
		this.addOperator(ConditionOperators.In, () => false);

		/**
		 * Override notIn operator, supports nothing
		 * As no use case at all
		 */
		this.addOperator(ConditionOperators.NotIn, () => false);
	}
}

/**
 * `json-rules-engine` typically expects a value property in conditions,
 * which can cause issues if you want to use these operators without specifying a value.
 * To override or handle a rule with no value, you can either use a workaround like setting value: null
 */
const updateConditionValues = (
	conditions: TopLevelCondition
): TopLevelCondition => {
	return Object.entries(conditions).reduce(
		(obj, [key, condition]: [string, Conditions[]]) => {
			return {
				...obj,
				[key]: condition.map((c: Conditions) => {
					return Object.entries(c).reduce(
						(o, [k, v]: [string, ConditionProperties[]]) => {
							return {
								...o,
								[k]: v.map((e) => ({
									...e,
									value: e.value === undefined ? null : e.value,
								})),
							};
						},
						{} as Conditions
					);
				}),
			};
		},
		{} as TopLevelCondition
	);
};

const evaluateRule = async (conditions: TopLevelCondition, facts: any) => {
	try {
		const engine = new Engine(
			[
				{
					conditions: updateConditionValues(conditions) as TLC,
					event: {
						type: 'isValid',
						params: {
							message: 'Facts are valid',
						},
					},
				},
			],
			{
				allowUndefinedFacts: true,
				allowUndefinedConditions: true,
			}
		);

		const result = await engine.run(facts);

		console.debug('[evaluateRule] input:', conditions, facts);
		console.debug('[evaluateRule] result:', result);

		return result.events.some((event) => event.type === 'isValid');
	} catch (e) {
		console.debug('[evaluateRule] input:', conditions, facts);
		console.error('[evaluateRule] error:', e);
		return false;
	}
};

export { Engine, evaluateRule };
